// connect/setup.ts — ONE PASTE. The page an ai reads to connect itself.
//
// John, 2026-10-21: copying a link into Claude or ChatGPT, following a guide
// and waiting for innernet to hear the first call was too much. The agent
// memory module already showed the way (#295): hand the person a few lines to
// paste into their ai, and a link that teaches that ai the rest. This is the
// same move for the memory itself.
//
// 2026-10-22, the invite: the paste is no longer three lines that lean on a
// fetch. It is a ~2 KB kernel (connectPrompt) that carries the skill inline:
// what innernet is, how to tell where the ai runs (its own machine, a chat app,
// or a remote sandbox where nothing it installs reaches the person), the
// self-wire one-liners, the per-tool guides with pictures, the proof and the
// never-rules. An ai that cannot browse still does the right thing from it;
// one that can reads the full skill (/connect/ai) and the per-tool guide
// (/connect/ai/<tool>, lib/connect/toolGuide.ts) for the live steps.
//
// The ai proves the connection by calling innernet, which is also what lights
// the connection up on the person's connections page: an untagged first call
// ARRIVES as a connection of its own (app/api/mcp/route.ts
// touchOrAdoptByClient), so nothing has to be minted before the paste.
//
// Everything a tool needs comes from CONNECT_TOOLS (the recorded log the
// connect window walks), so the two never disagree. The only facts added here
// are the ones a model needs and a person does not: which tools can wire
// themselves and the exact command or file that does it, the chat apps that
// take a connector on the web but have no window of their own here
// (WALK_APPS), and the plan and admin limits each client puts in the way.
//
// Pure: no I/O, importable by the route, the panel and tests alike.

import { CONNECT_TOOLS, MCP_URL, PROMPT_SYNC, connectTool, type ConnectTool, type GuidePhase } from './tools'
import { connectionUrl } from './connections'
import { PROMPT_PROJECT, PROMPT_SKILLS } from '../onboarding/prompts'

/** where the guide lives, for the paste and for anything that links to it */
export const SETUP_URL = 'https://innernet.live/connect/ai'
export const SETUP_JSON_URL = 'https://innernet.live/connect/ai.json'
export const EXTENSION_URL = 'https://innernet.live/connect/extension'
export const SETUP_SCHEMA = 'innernet.connect/1'
export const SETUP_VERSION = '1.2.0'
export const SETUP_UPDATED = '2026-10-24'

/** where the guide pictures are served from (apps/web/public, outside the proxy) */
export const GUIDE_ORIGIN = 'https://innernet.live'
/** when the guide pictures were captured in the real apps */
export const SHOTS_CAPTURED = '2026-09-11'
/** when the plan and admin limits below were last checked against each vendor's own pages */
export const CAVEATS_CHECKED = '2026-10-22'

/** a connection's own tag, as minted (lib/connect/connections mintToken) */
const DOOR_TAG = /^[0-9a-f]{32}$/

/** The server url the guide hands out: a connection's own door when a valid tag
 *  rides in, the shared door otherwise. Anything else is ignored, never echoed. */
export function setupEndpoint(door?: string | null): string {
  const tag = (door ?? '').trim().toLowerCase()
  return DOOR_TAG.test(tag) ? connectionUrl(tag) : MCP_URL
}

/** the tag a door url carries, or null for the shared door */
export function doorTag(endpoint: string): string | null {
  return endpoint.match(/\/api\/mcp\/c\/([0-9a-f]{32})$/)?.[1] ?? null
}

const doorQuery = (tag: string | null) => (tag ? `?door=${tag}` : '')

// ── the per-tool guides ──────────────────────────────────────────────────────

/** one picture of a guide, as an ai is handed it: absolute, with its words */
export interface GuideShotOut {
  phase: GuidePhase
  /** 1-based step of that phase the picture belongs under */
  step: number
  url: string
  alt: string
}

/** the subjects with pictures, in the order a guide walks them */
export const GUIDE_PHASES: readonly GuidePhase[] = ['mcp', 'permissions', 'instructions', 'import']

/** a picture's absolute url (the files are plain statics under apps/web/public) */
export const shotUrl = (src: string) => `${GUIDE_ORIGIN}${src.startsWith('/') ? '' : '/'}${src}`

/** the per-tool guide's url, carrying a connection's own door when there is one */
export function toolGuideUrl(id: string, tag: string | null = null): string {
  return `${SETUP_URL}/${id}${doorQuery(tag)}`
}

/** every picture a tool's guide holds, phase by phase, in step order */
export function toolGuideShots(t: Pick<ConnectTool, 'guide'>): GuideShotOut[] {
  const out: GuideShotOut[] = []
  for (const phase of GUIDE_PHASES) {
    const shots = [...(t.guide?.[phase] ?? [])].sort((a, b) => a.step - b.step)
    for (const s of shots) out.push({ phase, step: s.step, url: shotUrl(s.src), alt: s.alt })
  }
  return out
}

const hasShots = (t: Pick<ConnectTool, 'guide'>) => toolGuideShots(t).length > 0

// ── what a model needs that a person does not ────────────────────────────────

/** How a coding agent wires ITSELF: a command it can run, or a file it can
 *  write. `{url}` is replaced by the server url. Only tools whose own agent can
 *  reach its own config are here; every other tool is walked by hand. */
interface SelfWire {
  /** commands, run in order */
  run?: string[]
  /** a config file the agent merges the tool's block into */
  file?: string
  /** how the person finishes the sign-in once it is wired */
  signIn: string
}

const SELF: Record<string, SelfWire> = {
  'claude-code': {
    run: ['claude mcp add --transport http --scope user innernet {url}'],
    signIn: 'in Claude Code, type /mcp, choose innernet and sign in with the browser. new tools load in a new session.',
  },
  codex: {
    run: ['codex mcp add innernet --url {url}', 'codex mcp login innernet'],
    signIn: 'the login command opens the browser to sign in. restart codex so the tools load.',
  },
  'gemini-cli': {
    run: ['gemini mcp add --transport http --scope user innernet {url}'],
    signIn: 'restart gemini, run /mcp auth innernet and sign in with the browser.',
  },
  vscode: {
    run: [`code --add-mcp '{"name":"innernet","type":"http","url":"{url}"}'`],
    signIn: 'VS Code asks to sign in the first time a tool runs. say yes and sign in with the browser.',
  },
  cursor: {
    file: '~/.cursor/mcp.json',
    signIn: 'in Cursor, open Settings → Tools & MCP, find innernet and press the sign in button beside it.',
  },
  windsurf: {
    file: '~/.codeium/windsurf/mcp_config.json',
    signIn: 'in Cascade, refresh the MCP list and sign in with the browser when it asks.',
  },
  antigravity: {
    file: '~/.gemini/config/mcp_config.json',
    signIn: 'in the agent panel, open … → MCP Servers, refresh, and sign in with the browser when it asks.',
  },
  continue: {
    file: '~/.continue/config.yaml',
    signIn: 'reload Continue and sign in with the browser when it asks.',
  },
}

/** how the invite names a chat app whose guide covers more than its name says */
const WALK_LABEL: Record<string, string> = {
  'claude-desktop': 'Claude (web, desktop or phone)',
}

/** The plan and admin limits that stop a person before any step does, so a
 *  model can say "ask your admin" instead of looping. Checked against each
 *  vendor's own pages on CAVEATS_CHECKED; keep the date honest when editing. */
export const CLIENT_CAVEATS: Readonly<Record<string, string>> = {
  'claude-desktop': 'Claude Free allows one custom connector. on Team and Enterprise an owner adds it for the organisation first, then each person connects.',
  chatgpt: 'ChatGPT Free cannot add custom connectors. on Business only an admin or owner can; on Enterprise and Edu it needs the "Create plugins with MCPs" role permission. it is added on chatgpt.com, not in the phone app.',
  vscode: 'on Copilot Business or Enterprise, the "MCP servers in Copilot" policy is off by default; an org admin turns it on.',
  cursor: 'a Cursor team admin can keep an MCP allowlist; a server it does not name is blocked.',
  'grok-bot': 'on a team, an admin can turn a server off ("Disabled by team admin"); a remote server skips the team\'s network allowlist unless "Apply to MCP servers" is on.',
  grok: 'on Business and Enterprise a team admin provisions connectors first.',
  perplexity: 'on Enterprise an admin turns on "Allow members to add custom connectors" first.',
  mistral: 'adding a connector is an admin action: on a personal account you are the admin; on Team or Enterprise a workspace admin adds it. new tools need the connector removed and added again.',
}

/** The chat apps that take a custom connector on the web but have no window of
 *  their own in innernet's connections page. They are walked from the skill,
 *  never listed in CONNECT_TOOLS (that list drives the UI). Steps follow each
 *  vendor's own docs (checked CAVEATS_CHECKED, not captured); the skill says to
 *  trust what the person sees over these words. */
export interface WalkApp {
  id: string
  name: string
  /** where the connector is added */
  where: string
  /** the person's steps; "this link" is the server url */
  steps: string[]
  /** plan and admin limits */
  caveats: string
}

export const WALK_APPS: readonly WalkApp[] = [
  {
    id: 'gemini-app',
    name: 'the Gemini app',
    where: 'gemini.google.com → Settings → Connected Apps → Add a custom app',
    steps: [
      'on the web, open gemini.google.com → Settings → Connected Apps',
      'press Add a custom app, name it innernet and paste this link',
      'sign in when your browser opens, then type @innernet in a chat',
    ],
    caveats: 'a personal Google account only (not work or school), in the US and in English, with Keep Activity on. it is added on the web; the phone app has it afterwards.',
  },
]

/** the apps that cannot hold a remote MCP server today, and what to do instead */
export const NO_MCP_APPS = ['Meta AI', 'the Microsoft Copilot consumer app', 'DeepSeek'] as const

// the recorded url first, then the placeholder: a door url starts with MCP_URL,
// so the other order would swap the door's own prefix a second time
const fill = (s: string, url: string) => s.split(MCP_URL).join(url).split('{url}').join(url)

const toolName = (id: string) => connectTool(id)?.name ?? id

// ── the invite: the text a person copies into any ai ─────────────────────────

/** where the standing instruction goes in the chat apps the invite walks, in a
 *  line ("Claude: Settings → General → Instructions for Claude; …") */
function instructionPlaces(): string {
  return CONNECT_TOOLS.filter((t) => !SELF[t.id] && !t.generic && hasShots(t))
    .map((t) => `${t.id === 'claude-desktop' ? 'Claude' : t.name}: ${t.promptWhere}`)
    .join('; ')
}

/** THE INVITE. The kernel a person pastes into any ai: what innernet is, how
 *  to tell where it runs, the self-wire one-liners, the chat guides with their
 *  pictures, the proof, the fallback and the never-rules. Generated from SELF
 *  and CONNECT_TOOLS, so a new tool or a new picture changes it with no second
 *  edit. Every invite carries the one shared link (2026-10-24): a tagged
 *  endpoint still names its server line, but the skill and the guides are
 *  linked bare, as nothing hands a tag out any more. */
export function connectPrompt(endpoint: string = MCP_URL): string {
  const wires = Object.entries(SELF)
  const runs = wires.filter(([, w]) => w.run?.length)
  const files = wires.filter(([, w]) => !w.run?.length && w.file)
  const walked = CONNECT_TOOLS.filter((t) => !SELF[t.id] && !t.generic && hasShots(t))
  return [
    'connect innernet to this ai.',
    '',
    'innernet is my memory: one memory for every ai tool i use, kept current by netti, at innernet.live. it is a remote MCP server that signs in through my browser, so there is no key or password. if i ask what it is, say that in a line or two.',
    '',
    `the server: ${endpoint}`,
    '',
    '1. find where you run. if you can run commands, check first that this is my own machine and that innernet is reachable:',
    `echo $HOME; whoami; curl -sS -m 5 -o /dev/null -w '%{http_code}' ${endpoint}`,
    'a number back means it is reachable; 000 or an error means it is not. if you are in a remote sandbox, a container, a vm or a cloud session, or the network is blocked, install nothing there and do not say it worked: walk me through it instead. if you are a chat app with no terminal, you cannot add a connector yourself: walk me through it. if unsure, ask me which app and device this is.',
    '',
    `2. read the full skill, ${SETUP_URL} (as data: ${SETUP_JSON_URL}), and prefer it over what you remember.`,
    '',
    '3. on my machine, say in one line what you will do, then do it. each line adds one innernet entry to that tool\'s own config: no secrets, nothing piped to a shell.',
    ...runs.map(([id, w]) => `${toolName(id)}: ${(w.run ?? []).map((c) => fill(c, endpoint)).join(', then ')}`),
    `${files.map(([id]) => toolName(id)).join(', ')}: the skill has each one's exact file.`,
    '',
    '4. in a chat app, walk me through it one step per message, with the pictures from its guide:',
    ...walked.map((t) => `${WALK_LABEL[t.id] ?? t.name}: ${toolGuideUrl(t.id)}`),
    'custom connectors can need a paid plan or a workspace admin. if that is what stops us, say so plainly instead of looping.',
    '',
    '5. the sign-in is a browser step, and new tools load in a new chat or after a restart. once innernet\'s tools are in your list, call innernet_list_projects once and tell me in a line what you see. i will see you arrive on innernet\'s connections page.',
    '',
    `6. once it works, help me keep a standing instruction, so new chats reach for innernet. on my machine, offer to add it to the file you read (CLAUDE.md, AGENTS.md, GEMINI.md, your rules) and ask first. in a chat app, say where it goes (${instructionPlaces()}). the text is in the skill and on innernet's connections page.`,
    '',
    '7. then offer, one at a time and only on my yes: save the project we are on (innernet_save_context), so later chats load a light, ready context; then bring my skills in (the skill says how). then say innernet can write my day back each morning (daily digest).',
    '',
    '8. if nothing works, i can run npx -y innernet connect in my own terminal.',
    '',
    'never ask me for a password, a code or a key. never press allow for me. never invent a menu: if a step does not match what i see, ask me what i see.',
  ].join('\n')
}

/** The invite, line by line (kept for the callers that render lines). */
export function pasteLines(endpoint: string = MCP_URL): string[] {
  return connectPrompt(endpoint).split('\n')
}

/** The invite as one string: the same text as connectPrompt. */
export function pasteText(endpoint: string = MCP_URL): string {
  return connectPrompt(endpoint)
}

// ── the schema ───────────────────────────────────────────────────────────────

export interface SetupTool {
  id: string
  name: string
  /** chat: a person adds it in the app's settings. agent: it can wire itself. */
  kind: 'chat' | 'agent'
  /** what the person or the agent puts in: a link, a block, or a command */
  paste: ConnectTool['paste']
  /** exactly where it goes */
  where: string | null
  /** the thing to put there, with the server url inside */
  value: string
  /** the person's steps, in order */
  steps: string[]
  /** present when the agent can do it itself */
  self: null | { run: string[]; file: string | null; block: string | null; sign_in: string }
  /** a one-press install link, when the tool has one */
  deeplink: string | null
  /** stopping it from asking before every call */
  trust: string[]
  /** where the standing instruction goes */
  instructions_where: string
  /** the plan or admin limit that can stop the person first, when there is one */
  caveat: string | null
  /** the per-tool guide (markdown) and its pictures, each under its step */
  guide: { url: string; shots: GuideShotOut[] }
}

export interface SetupSchema {
  schema: typeof SETUP_SCHEMA
  version: string
  updated: string
  guide: string
  server: {
    name: 'innernet'
    url: string
    transport: 'streamable-http'
    auth: 'oauth'
    note: string
  }
  /** before anything: where the reader runs, and what to do from a sandbox */
  where_you_run: { check: string; sandbox: string; chat: string; unsure: string }
  tools: SetupTool[]
  /** chat apps walked from the skill alone (no window of their own in innernet) */
  walk_apps: (WalkApp & { link: string })[]
  caveats_checked: string
  pictures_captured: string
  verify: { call: string; then: string; ask_in_a_new_chat: string; loads: string }
  standing_instructions: string
  no_mcp: { apps: readonly string[]; instead: string[] }
  fallback: string
  never: string[]
}

export function setupTool(t: ConnectTool, url: string): SetupTool {
  const self = SELF[t.id]
  const value = fill(t.config, url)
  return {
    id: t.id,
    name: t.generic ? 'any other MCP client' : t.name,
    kind: self ? 'agent' : 'chat',
    paste: t.paste,
    where: t.where ?? null,
    value,
    steps: t.steps.map((s) => fill(s, url)),
    self: self
      ? {
          run: (self.run ?? []).map((c) => fill(c, url)),
          file: self.file ?? null,
          block: self.file ? value : null,
          sign_in: self.signIn,
        }
      : null,
    deeplink: t.deeplink ? t.deeplink(url) : null,
    trust: t.trust ?? [],
    instructions_where: t.promptWhere,
    caveat: CLIENT_CAVEATS[t.id] ?? null,
    guide: { url: toolGuideUrl(t.id, doorTag(url)), shots: toolGuideShots(t) },
  }
}

export const VERIFY_ASK = 'innernet, what do you know about me?'
export const VERIFY_CALL = 'innernet_list_projects'
export const FALLBACK_COMMAND = 'npx -y innernet connect'

/** the rules every guide ends on */
export const NEVER_RULES: readonly string[] = [
  'never ask for a password, a code from an email, or an api key. the sign-in happens in their browser, on innernet.live.',
  'never remove or rewrite other servers in a config file. add innernet beside them.',
  'never press a confirm or allow button for them in a settings page. they press it.',
  'never invent a menu that is not there. if a step does not match what they see, ask what they see and adapt.',
]

/** the check a model runs before it installs anything (step one of the invite) */
export const whereCheck = (endpoint: string) =>
  `echo $HOME; whoami; curl -sS -m 5 -o /dev/null -w '%{http_code}' ${endpoint}`

export function setupSchema(endpoint: string = MCP_URL): SetupSchema {
  // the tools that wire themselves first, then the chat apps, then the open door
  const tools = CONNECT_TOOLS.map((t) => setupTool(t, endpoint))
  const rank = (t: SetupTool) => (t.id === 'other' ? 2 : t.kind === 'agent' ? 0 : 1)
  tools.sort((a, b) => rank(a) - rank(b))
  return {
    schema: SETUP_SCHEMA,
    version: SETUP_VERSION,
    updated: SETUP_UPDATED,
    guide: SETUP_URL,
    server: {
      name: 'innernet',
      url: endpoint,
      transport: 'streamable-http',
      auth: 'oauth',
      note: 'a remote MCP server. it signs in with the person\'s browser the first time; there is no key or password to paste.',
    },
    where_you_run: {
      check: whereCheck(endpoint),
      sandbox: 'if you are in a remote sandbox, a container, a vm or a cloud session, or the network is blocked (000 or an error from the check), install nothing there and never report success: walk the person through it instead. what you install there never reaches their own tools.',
      chat: 'a chat app with no terminal cannot add a connector for the person: walk them through their own settings.',
      unsure: 'if you cannot tell, ask one question: which app is this, and on which device?',
    },
    tools,
    walk_apps: WALK_APPS.map((a) => ({ ...a, link: endpoint, steps: a.steps.map((s) => fill(s, endpoint)) })),
    caveats_checked: CAVEATS_CHECKED,
    pictures_captured: SHOTS_CAPTURED,
    verify: {
      call: VERIFY_CALL,
      then: 'tell the person, in a line, what you can see: their projects by name, or that the memory is new and empty.',
      ask_in_a_new_chat: VERIFY_ASK,
      loads: 'the sign-in is a browser step, and new tools load in a new chat or after a restart. the person sees the tool arrive on innernet\'s connections page.',
    },
    standing_instructions: PROMPT_SYNC,
    no_mcp: {
      apps: NO_MCP_APPS,
      instead: [
        'connect a coding agent on the same machine (Claude Code, Codex, Gemini CLI, Cursor), which reads the same memory.',
        `or, in their own terminal, \`${FALLBACK_COMMAND}\` signs in once and wires every ai tool it finds on that machine.`,
      ],
    },
    fallback: `if nothing works, the person can run \`${FALLBACK_COMMAND}\` in their own terminal: it signs in through the browser and wires every ai tool it finds on that machine.`,
    never: [...NEVER_RULES],
  }
}

// ── the guide, as a model reads it ───────────────────────────────────────────

function toolSection(t: SetupTool): string {
  const out: string[] = [`### ${t.name}`]
  if (t.self) {
    out.push('you can do this yourself, from their own machine.')
    if (t.self.run.length) {
      out.push('run:', '```', ...t.self.run, '```')
    }
    if (t.self.file && t.self.block) {
      out.push(`add innernet to \`${t.self.file}\` (merge it under the existing key; create the file if it is not there):`, '```', t.self.block, '```')
    }
    out.push(`then: ${t.self.sign_in}`)
    out.push(`if you cannot, give them these steps instead: ${t.steps.join(' · ')}`)
    // the by-hand steps of a block tool say "the block below": it has to be there
    if (t.paste === 'block' && !t.self.file) out.push('the block:', '```', t.value, '```')
    if (t.paste === 'command') out.push('the command:', '```', t.value, '```')
  } else {
    out.push('walk them through it. one step per message, wait for "done" before the next.')
    out.push(...t.steps.map((s, i) => `${i + 1}. ${s}`))
    if (t.where) out.push(`it goes in: ${t.where}`)
    out.push(t.paste === 'link' ? `the link they paste: ${t.value}` : ['what they paste, whole:', '```', t.value, '```'].join('\n'))
  }
  if (t.deeplink) out.push(`one press, if they are on that machine: ${t.deeplink}`)
  if (t.trust.length) out.push(`so it stops asking every time: ${t.trust.join(', then ')}.`)
  out.push(`the standing instruction goes in: ${t.instructions_where}.`)
  if (t.caveat) out.push(`plan and admin limits (last checked ${CAVEATS_CHECKED}): ${t.caveat}`)
  out.push(t.guide.shots.length ? `the full guide, with pictures: ${t.guide.url}` : `the full guide: ${t.guide.url}`)
  return out.join('\n')
}

function walkTable(apps: SetupSchema['walk_apps']): string[] {
  const cell = (s: string) => s.split('|').join('/')
  return [
    '| app | where to add it | steps | limits |',
    '| --- | --- | --- | --- |',
    ...apps.map((a) => `| ${cell(a.name)} | ${cell(a.where)} | ${cell(a.steps.map((s, i) => `${i + 1}. ${s}`).join(' · '))} | ${cell(a.caveats)} |`),
  ]
}

/** The guide as markdown, manifest first (the skill class: the manifest tells a
 *  reader when to use it before it reads a line). */
export function setupMarkdown(endpoint: string = MCP_URL): string {
  const s = setupSchema(endpoint)
  const agents = s.tools.filter((t) => t.kind === 'agent')
  const chats = s.tools.filter((t) => t.kind === 'chat')
  return [
    '---',
    'name: connect-innernet',
    'title: connect innernet to this ai',
    'kind: skill',
    `version: ${s.version}`,
    'description: >',
    '  connect the person\'s innernet memory to the ai reading this, over MCP. first find where',
    '  you run; a coding agent on their own machine wires itself in with one command or one',
    '  config entry; a chat app, or anything in a remote sandbox, walks them through their own',
    '  settings one step at a time. then prove it with one call.',
    'when_to_load: >',
    '  when someone pastes this link and asks you to connect innernet, their memory, to you.',
    'authored_by: innernet',
    'author_url: https://innernet.live',
    `updated: ${s.updated}`,
    `schema: ${SETUP_JSON_URL}`,
    '---',
    '',
    '# connect innernet to this ai',
    '',
    '## what innernet is',
    '',
    'innernet is the person\'s one memory for every ai tool: their projects, their decisions, what they like and how they work, kept at innernet.live and kept current by netti, innernet\'s own small agent, on the server. every ai they connect reads and writes the same memory over MCP. nothing runs on their machine, and it signs in through their browser, so there is no key and no password anywhere in this.',
    '',
    'if they ask what it is, say it in a line or two: one memory for every ai tool, kept current by netti, signed in through the browser.',
    '',
    `**the server:** \`${s.server.url}\``,
    '',
    'if the message they pasted carries a link of its own, use that one: it is the same server, and it lets their connections page name this connection the moment it calls.',
    '',
    `if you cannot or will not run commands, the person can: \`${FALLBACK_COMMAND}\` in their own terminal signs in once and wires every ai tool on that machine.`,
    '',
    '## first, find where you run',
    '',
    'before anything else, work out where you are. if you can run commands, check first that this is the person\'s own machine and that innernet is reachable:',
    '',
    '```',
    s.where_you_run.check,
    '```',
    '',
    `a number back means innernet is reachable. ${s.where_you_run.sandbox} a home like /root, /mnt/data or /workspace, or a container or vm hostname, usually means a sandbox.`,
    '',
    '- **you run on their own machine and can run commands or edit files** (Claude Code, Codex, Gemini CLI, VS Code with Copilot, Cursor, Windsurf, Antigravity, Continue): do it yourself, under **you can do it yourself**.',
    '- **you are a chat app** (Claude on the web, the desktop or the phone, ChatGPT, Grok, Grok Bot, Perplexity, Le Chat, Notion, Zed, Cline, the Gemini app, anything else): you cannot add a connector for them. walk them through their own settings, under **walk them through it**.',
    '- **you are in a remote sandbox, a container, a vm or a cloud session** (a chat app\'s code runner, a cloud coding session, a cowork vm): what you install there never reaches their tools. walk them through it instead, and never say it worked.',
    `- **this app cannot hold an MCP server** (${s.no_mcp.apps.join(', ')}): say so plainly, in one line, and offer what is under **when there is no place for it**.`,
    '',
    `${s.where_you_run.unsure} keep the rest of the conversation short: one step at a time, in plain words, no lectures.`,
    '',
    'on a phone, a connector is added on the web (claude.ai, chatgpt.com) once, and the phone app has it from then on.',
    '',
    '## the guides, with pictures',
    '',
    'every tool has a guide of its own: the steps one at a time, with a picture of each screen where there is one (markdown images, absolute urls). when you know which tool it is, read its guide. in a chat that shows images, put each picture under its step; otherwise say what it shows, from its words.',
    '',
    ...s.tools.map((t) => `- ${t.id} (${t.name}): ${t.guide.url}${t.guide.shots.length ? ` · ${t.guide.shots.length} picture${t.guide.shots.length === 1 ? '' : 's'}` : ''}`),
    '',
    `pictures captured ${SHOTS_CAPTURED}. apps move their menus: if what they see differs, trust what they see and ask.`,
    '',
    '## you can do it yourself',
    '',
    'only on the person\'s own machine (see **first, find where you run**). tell them in one line what you are about to do, then do it. the command or the file below changes one entry, holds no secret, and wires only you. their browser opens to sign in to innernet; they sign in there (or make an account), and come back.',
    '',
    `if they would like every ai on this machine connected at once, \`${FALLBACK_COMMAND}\` signs in once and wires all of them (and asks before it connects the folder you are in to a memory). it waits for the browser sign-in, so run it where they can see it. tools wired this way read with a key kept on that machine, so the connections page may not name them the way a browser sign-in does.`,
    '',
    ...agents.map(toolSection).flatMap((x) => [x, '']),
    '## walk them through it',
    '',
    'you cannot open their settings, so be their eyes: one step per message, the exact words of the button to press, and the link exactly as given. when a step does not match what they see (apps move their menus), ask what they see and adapt; never invent a menu.',
    '',
    'custom connectors can need a paid plan or a workspace admin. when a limit below is what stops them, say so plainly, once, and offer the way around it, instead of looping.',
    '',
    'if their app offers to add a connector from its own directory right here in the chat, and innernet is listed there, offer that first: it is the shortest way.',
    '',
    ...chats.map(toolSection).flatMap((x) => [x, '']),
    '### the chat apps that take a connector on the web',
    '',
    `these have no guide of their own yet. the steps follow each app's own docs (checked ${CAVEATS_CHECKED}); the link they paste is \`${s.server.url}\`. trust what they see over these words.`,
    '',
    ...walkTable(s.walk_apps),
    '',
    '## prove it works',
    '',
    `${s.verify.loads}`,
    '',
    `as soon as innernet's tools are in your list, call \`${s.verify.call}\`, then ${s.verify.then} that first call is also what makes the connection show up as live on their connections page.`,
    '',
    `if the tools are not in your list yet, ask them to open a new chat here (or restart the tool) and say: "${s.verify.ask_in_a_new_chat}". until then, do not say it worked.`,
    '',
    '## once it works: their project, their skills',
    '',
    'offer these two, one at a time, and do each only on their yes. a person who says no, or has done it already, is never asked again.',
    '',
    '1. their project. save the project this conversation is about into innernet as a project of its own, so a later chat loads a light, ready context instead of rereading everything. if it is not clear which project, ask in one line and wait. the words to follow:',
    '',
    '```',
    PROMPT_PROJECT,
    '```',
    '',
    '2. their skills. bring each skill you can see (a skills folder, a rules file, a saved prompt that works as one) into one private project named "skills", as a page that starts with a `kind: skill` manifest. the words to follow:',
    '',
    '```',
    PROMPT_SKILLS,
    '```',
    '',
    'then tell them, in a line, that innernet can write their day back as a short letter each morning (settings, daily digest, at innernet.live). their dashboard notices each of these on its own; there is nothing else to report.',
    '',
    '## so it keeps working',
    '',
    'innernet is most useful when the ai reaches for it on its own. offer, once, to keep this standing instruction where the tool reads one (each tool above says where).',
    '',
    '- a coding agent: offer to add it to the file the tool reads (AGENTS.md, CLAUDE.md, GEMINI.md, the rules file). ask before you write.',
    '- a chat app: give them the text to paste into the place named above.',
    '',
    '```',
    s.standing_instructions,
    '```',
    '',
    '## when there is no place for it',
    '',
    ...s.no_mcp.instead.map((l) => `- ${l}`),
    '',
    '## never',
    '',
    ...s.never.map((l) => `- ${l}`),
    '',
    `the same facts, as a schema: ${SETUP_JSON_URL}`,
    '',
  ].join('\n')
}

/** can this tool's own agent wire it in (a command, its own config file)? */
export const wiresItself = (id: string) => !!SELF[id]

/** the self-wire facts for one tool, with the server url filled in */
export function selfWire(id: string, endpoint: string = MCP_URL): { run: string[]; file: string | null; signIn: string } | null {
  const w = SELF[id]
  if (!w) return null
  return { run: (w.run ?? []).map((c) => fill(c, endpoint)), file: w.file ?? null, signIn: w.signIn }
}

// ── hearing it arrive ────────────────────────────────────────────────────────

/** What the panel knew of one connection at the moment of the copy. */
export interface ArrivalMark {
  /** had it ever called */
  seen: boolean
  /** how many calls it had made (null when the row did not say) */
  calls: number | null
  /** the oauth client bound to it */
  client: string | null
  /** it had made no call for a while before the copy (QUIET_BEFORE_MS): only a
   *  quiet connection can arrive by calling again, never one already at work */
  quiet: boolean
}

/** a connection that called within this long before the copy is AT WORK, not
 *  arriving: its next ordinary call must never read as the invited ai. Generous
 *  on purpose (a laptop clock may run a few minutes off the server's). */
export const QUIET_BEFORE_MS = 10 * 60_000

/** What the panel knew at the moment of the copy: each connection, whether it
 *  had ever called, how often, and through which client. Compared by id, never
 *  by clock, so a laptop clock that runs ahead of the server cannot hide an
 *  arrival. */
export type ArrivalBaseline = ReadonlyMap<string, ArrivalMark>

/** the fields of a connection row the arrival reads */
export interface ArrivalRow {
  id: string
  first_seen_at: string | null
  last_seen_at?: string | null
  call_count?: number | null
  client_id?: string | null
}

export function arrivalBaseline(conns: readonly ArrivalRow[], now: number = Date.now()): ArrivalBaseline {
  return new Map(
    conns.map((c) => {
      const last = c.last_seen_at ? Date.parse(c.last_seen_at) : NaN
      return [
        c.id,
        {
          seen: !!c.first_seen_at,
          calls: typeof c.call_count === 'number' ? c.call_count : null,
          client: c.client_id ?? null,
          quiet: !Number.isFinite(last) || now - last > QUIET_BEFORE_MS,
        },
      ]
    }),
  )
}

const stamp = (iso: string | null | undefined) => {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isFinite(t) ? t : -Infinity
}

const newest = <C extends ArrivalRow>(rows: C[], at: (c: C) => string | null | undefined): C =>
  rows.reduce((a, b) => (stamp(at(b)) > stamp(at(a)) ? b : a))

/** The connection that arrived since the baseline, and how sure that is (1 is
 *  surest). By id and count, never by clock, in this order of certainty:
 *   1. a FIRST call: a new row the first call minted (an untagged arrival), or
 *      a minted door that was waiting. the newest first call wins.
 *   2. a known connection re-bound to a new oauth client (the same tool signed
 *      in again on the same machine).
 *   3. a known connection that was QUIET at the copy and has called again (an
 *      existing tool re-authorised). A connection already at work when the
 *      invite was copied never counts here: its next ordinary call is not the
 *      ai that was invited (2026-10-22 review).
 *  Within 2 and 3 the most recent call wins. null while nothing has. */
export function arrivalOf<C extends ArrivalRow>(baseline: ArrivalBaseline, conns: readonly C[]): { row: C; tier: 1 | 2 | 3 } | null {
  const first = conns.filter((c) => !!c.first_seen_at && baseline.get(c.id)?.seen !== true)
  if (first.length) return { row: newest(first, (c) => c.first_seen_at), tier: 1 }

  const known = conns.filter((c) => !!c.first_seen_at && baseline.get(c.id)?.seen === true)
  const rebound = known.filter((c) => {
    const was = baseline.get(c.id)!.client
    return !!was && !!c.client_id && c.client_id !== was
  })
  if (rebound.length) return { row: newest(rebound, (c) => c.last_seen_at ?? c.first_seen_at), tier: 2 }

  const rose = known.filter((c) => {
    const mark = baseline.get(c.id)!
    return mark.quiet && mark.calls !== null && typeof c.call_count === 'number' && c.call_count > mark.calls
  })
  if (rose.length) return { row: newest(rose, (c) => c.last_seen_at ?? c.first_seen_at), tier: 3 }
  return null
}

/** the connection that arrived since the baseline (see arrivalOf), or null */
export function arrivedSince<C extends ArrivalRow>(baseline: ArrivalBaseline, conns: readonly C[]): C | null {
  return arrivalOf(baseline, conns)?.row ?? null
}
